import { InfoIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { useModal, type ModalProps } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  useGetMcpServersQuery,
  useGetWorkspaceMcpServerOverridesQuery,
  useToggleWorkspaceMcpServerOverrideMutation,
  type McpServer,
} from '@generated/graphql';
import { Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import posthog from 'posthog-js';
import { useState } from 'react';
import { ResourceTag } from '../ResourceTag';
import { ResourceListItem, ResourceTransferListButtons } from '../ResourceTransferList';

type ManageImportedMcpServersModalProps = {
  organizationId: string;
  workspaceId: string;
  onChange?: VoidFunction;
};

const ManageImportedMcpServersModal = ({
  organizationId,
  workspaceId,
  onChange,
}: ManageImportedMcpServersModalProps) => {
  const { palette, breakpoints } = useTheme();
  const isVertical = useMediaQuery(breakpoints.down('md'));

  const [selectedCurrent, setSelectedCurrent] = useState<Set<string>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [transferring, setTransferring] = useState(false);

  const [toggleOverride] = useToggleWorkspaceMcpServerOverrideMutation();

  const { data: orgData, refetch: refetchOrgData } = useGetMcpServersQuery({
    variables: { mode: 'organization', organizationId },
    skip: !organizationId,
  });

  const { data: userData, refetch: refetchUserData } = useGetMcpServersQuery({
    variables: { mode: 'user' },
  });

  const { data: overridesData, refetch: refetchOverridesData } =
    useGetWorkspaceMcpServerOverridesQuery({
      variables: { workspaceId },
      skip: !workspaceId,
    });

  const overriddenMcpServerIds = new Set(
    (overridesData?.getWorkspaceMcpServerOverrides || []).map((o) => o?.mcpServerId),
  );

  const orgServers = orgData?.getMcpServers || [];
  const userServers = userData?.getMcpServers || [];

  const autoInheritedOrgMcpServers = orgServers.filter((s) => s.enabled);
  const autoInheritedUserMcpServers = userServers.filter((s) => s.enabled);

  // Imported = not auto-inherited AND has override (currently included)
  const currentServers = [
    ...orgServers
      .filter((s) => !s.enabled && overriddenMcpServerIds.has(s.mcpServerId))
      .map((s) => ({ ...s, mcpSource: 'organization' as const })),
    ...userServers
      .filter((s) => !s.enabled && overriddenMcpServerIds.has(s.mcpServerId))
      .map((s) => ({ ...s, mcpSource: 'user' as const })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Available = not auto-inherited AND no override (not yet imported)
  const availableServers = [
    ...orgServers
      .filter((s) => !s.enabled && !overriddenMcpServerIds.has(s.mcpServerId))
      .map((s) => ({ ...s, mcpSource: 'organization' as const })),
    ...userServers
      .filter((s) => !s.enabled && !overriddenMcpServerIds.has(s.mcpServerId))
      .map((s) => ({ ...s, mcpSource: 'user' as const })),
  ].sort((a, b) => a.name.localeCompare(b.name));

  const toggleCurrent = (id: string) => {
    setSelectedCurrent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAvailable = (id: string) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddServers = async () => {
    if (selectedAvailable.size === 0) return;
    setTransferring(true);
    try {
      await Promise.all(
        Array.from(selectedAvailable).map((mcpServerId) =>
          toggleOverride({
            variables: { workspaceId, mcpServerId },
            onCompleted: () => {
              posthog.capture('mcp_servers:user_add-workspace_mcp_servers_transfer', {
                workspaceId,
                mcpServerId,
              });
              void refetchOverridesData();
              void refetchOrgData();
              void refetchUserData();
              onChange?.();
            },
          }),
        ),
      );
      setSelectedAvailable(new Set());
    } finally {
      setTransferring(false);
    }
  };

  const handleRemoveServers = async () => {
    if (selectedCurrent.size === 0) return;
    setTransferring(true);
    try {
      await Promise.all(
        Array.from(selectedCurrent).map((mcpServerId) =>
          toggleOverride({
            variables: { workspaceId, mcpServerId },
            onCompleted: () => {
              posthog.capture('mcp_servers:user_remove-workspace_mcp_servers_transfer', {
                workspaceId,
                mcpServerId,
              });
              void refetchOverridesData();
              void refetchOrgData();
              void refetchUserData();
              onChange?.();
            },
          }),
        ),
      );
      setSelectedCurrent(new Set());
    } finally {
      setTransferring(false);
    }
  };

  const hasAutoInherited =
    autoInheritedOrgMcpServers.length > 0 || autoInheritedUserMcpServers.length > 0;

  return (
    <Stack gap={3} sx={{ height: '100%' }}>
      {/* Auto-inherited MCP servers section (read-only) */}
      {hasAutoInherited && (
        <Stack gap={1} sx={{ flexShrink: 0 }}>
          <Typography
            className="deckard-text-input-label"
            variant="caption2"
            color={palette.input.label.default}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Auto-inherited MCP servers
            <Tooltip title="Auto-inherited MCP servers are enabled by default for all workspaces. You can override this by clicking the toggle button in the MCP Servers panel.">
              <InfoIcon size={16} />
            </Tooltip>
          </Typography>
          <Group flexWrap="wrap" gap={1}>
            {autoInheritedOrgMcpServers.map((server) => (
              <ResourceTag key={server.mcpServerId} name={server.name} source="organization" />
            ))}
            {autoInheritedUserMcpServers.map((server) => (
              <ResourceTag key={server.mcpServerId} name={server.name} source="user" />
            ))}
          </Group>
        </Stack>
      )}

      {/* Transfer list */}
      <Stack
        direction={isVertical ? 'column' : 'row'}
        gap={2}
        sx={{ flex: 1, minHeight: '400px', maxHeight: isVertical ? '70vh' : '600px' }}
      >
        {/* Current Imported MCP Servers */}
        <Stack flex={1} sx={{ overflow: 'hidden' }} gap={2}>
          <Typography variant="body2" color="foreground.paper.light">
            Imported MCP servers ({currentServers.length})
          </Typography>
          <Stack
            sx={{
              flex: 1,
              overflow: 'auto',
              py: 1,
              backgroundColor: palette.background.paperCard,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {currentServers.length === 0 ? (
              <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
                <Typography variant="body2" color="foreground.paperCard.light">
                  No imported MCP servers
                </Typography>
              </Stack>
            ) : (
              currentServers.map((server) => (
                <ResourceListItem<McpServer>
                  key={server.mcpServerId}
                  resource={server}
                  idKey="mcpServerId"
                  tag={server.mcpSource === 'organization' ? 'Organization' : 'User'}
                  selected={selectedCurrent.has(server.mcpServerId)}
                  onToggle={toggleCurrent}
                />
              ))
            )}
          </Stack>
        </Stack>

        {/* Transfer Buttons */}
        <ResourceTransferListButtons
          onAdd={handleAddServers}
          onRemove={handleRemoveServers}
          isVertical={isVertical}
          addDisabled={selectedAvailable.size === 0 || transferring}
          removeDisabled={selectedCurrent.size === 0 || transferring}
        />

        {/* Available MCP Servers */}
        <Stack flex={1} sx={{ overflow: 'hidden' }} gap={2}>
          <Typography variant="body2" color="foreground.paper.light">
            Available MCP servers
          </Typography>
          <Stack
            sx={{
              flex: 1,
              overflow: 'auto',
              py: 1,
              backgroundColor: palette.background.paperCard,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {availableServers.length === 0 ? (
              <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
                <Typography variant="body2" color="foreground.paperCard.light">
                  No available MCP servers found
                </Typography>
              </Stack>
            ) : (
              availableServers.map((server) => (
                <ResourceListItem<McpServer>
                  key={server.mcpServerId}
                  resource={server}
                  idKey="mcpServerId"
                  tag={server.mcpSource === 'organization' ? 'Organization' : 'User'}
                  selected={selectedAvailable.has(server.mcpServerId)}
                  onToggle={toggleAvailable}
                />
              ))
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const useManageImportedMcpServersModal = ({
  componentProps,
  modalProps,
}: {
  componentProps: Omit<ManageImportedMcpServersModalProps, 'onClose'> & { onClose?: () => void };
  modalProps?: Partial<ModalProps>;
}) => {
  const { showModal } = useModal({
    component: ManageImportedMcpServersModal,
    componentProps,
    modalProps: {
      title: 'Manage imported MCP servers',
      sx: {
        width: '90vw',
        maxWidth: '1000px',
        display: 'flex',
        flexDirection: 'column',
      },
      ...modalProps,
    },
  });

  const showManageImportedMcpServersModal = (
    dynamicComponentProps?: Partial<ManageImportedMcpServersModalProps>,
  ) => {
    showModal({ componentProps: dynamicComponentProps });
  };

  return { showManageImportedMcpServersModal };
};
